import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import type { AxiosError } from "axios";
import { staffService } from "../services/staff.service";
import type {
	CreateStaffRequest,
	ErrorResponse,
	ListStaffParams,
	UpdateStaffRequest,
} from "../types";

// ─── Query Keys ─────────────────────────────────────────────────────
export const staffKeys = {
	all: ["staff"] as const,
	lists: () => [...staffKeys.all, "list"] as const,
	list: (params: ListStaffParams) => [...staffKeys.lists(), params] as const,
	details: () => [...staffKeys.all, "detail"] as const,
	detail: (id: string) => [...staffKeys.details(), id] as const,
};

// ─── useStaffList (list) ─────────────────────────────────────────────
export function useStaffList(params: ListStaffParams) {
	return useQuery({
		queryKey: staffKeys.list(params),
		queryFn: () => staffService.list(params),
		enabled: !!params.organizationId,
	});
}

// ─── useStaff (getById) ──────────────────────────────────────────────
export function useStaff(id: string) {
	return useQuery({
		queryKey: staffKeys.detail(id),
		queryFn: () => staffService.getById(id),
		enabled: !!id,
	});
}

// ─── useCreateStaff ─────────────────────────────────────────────────
export function useCreateStaff() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateStaffRequest) => staffService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: staffKeys.lists() });
			message.success("Xodim muvaffaqiyatli yaratildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Xodim saqlashda xatolik";
			message.error(msg);
		},
	});
}

// ─── useUpdateStaff ─────────────────────────────────────────────────
export function useUpdateStaff() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateStaffRequest }) =>
			staffService.update(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: staffKeys.lists() });
			queryClient.invalidateQueries({ queryKey: staffKeys.detail(variables.id) });
			message.success("Xodim muvaffaqiyatli yangilandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Xodimni yangilashda xatolik";
			message.error(msg);
		},
	});
}

// ─── useDeleteStaff ─────────────────────────────────────────────────
export function useDeleteStaff() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => staffService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: staffKeys.lists() });
			message.success("Xodim muvaffaqiyatli o'chirildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Xodimni o'chirishda xatolik";
			message.error(msg);
		},
	});
}
